import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { activities } from '@/mocks/data';

export default function ActivitiesPreview() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const previewActivities = activities.slice(0, 5);

  return (
    <section className="relative section-py bg-background-50">
      {/* Subtle top decorative line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-16 bg-gradient-to-b from-primary-300/40 to-transparent"></div>

      <div className="container-page">
        {/* Section header */}
        <div className="mb-16">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">
                {t('home.activitiesLabel')}
              </span>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-[1.1] tracking-tight">
                {t('home.activitiesTitle')}{' '}
                <span className="text-primary-500">{t('home.activitiesTitleHighlight')}</span>
              </h2>
            </div>
            <Link
              to="/faaliyetlerimiz"
              className="hidden lg:inline-flex items-center gap-2 text-sm font-semibold text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer group"
            >
              <span>Tümünü Görüntüle</span>
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </div>
        </div>

        {/* Activity cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
          {previewActivities.map((activity, index) => (
            <div
              key={activity.id}
              className={`relative group cursor-pointer rounded-2xl border transition-all duration-500 overflow-hidden ${
                activeIndex === index
                  ? 'border-primary-200 bg-primary-50/30 shadow-lg shadow-primary-100/20 scale-[1.02]'
                  : 'border-foreground-100/50 bg-background-50 hover:border-foreground-200 hover:shadow-md hover:shadow-foreground-100/10'
              }`}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              <div className="p-6 md:p-7 flex flex-col h-full min-h-[280px]">
                {/* Icon */}
                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl mb-6 transition-all duration-500 ${
                  activeIndex === index
                    ? 'bg-primary-500 text-background-50 shadow-lg shadow-primary-500/25'
                    : 'bg-foreground-50 text-foreground-500 group-hover:bg-primary-100 group-hover:text-primary-600'
                }`}>
                  <i className={`${activity.icon} text-2xl`}></i>
                </div>

                {/* Number */}
                <span className="text-[11px] font-bold tracking-widest text-foreground-300 mb-3 uppercase">
                  0{index + 1}
                </span>

                {/* Title */}
                <h3 className="text-base md:text-lg font-bold text-foreground-900 mb-3 leading-snug">
                  {activity.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-foreground-500 leading-relaxed mb-6 flex-1">
                  {activity.description}
                </p>

                {/* Link */}
                <Link
                  to="/faaliyetlerimiz"
                  className={`inline-flex items-center gap-2 text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                    activeIndex === index
                      ? 'text-primary-600'
                      : 'text-foreground-400 group-hover:text-primary-600'
                  }`}
                >
                  <span>Detaylı Bilgi</span>
                  <i className={`ri-arrow-right-line w-4 h-4 flex items-center justify-center transition-transform duration-300 ${
                    activeIndex === index ? 'translate-x-1' : ''
                  }`}></i>
                </Link>
              </div>

              {/* Active indicator line */}
              <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 transition-all duration-500 ${
                activeIndex === index ? 'opacity-100' : 'opacity-0'
              }`}></div>
            </div>
          ))}
        </div>

        {/* Mobile view all link */}
        <div className="mt-10 text-center lg:hidden">
          <Link
            to="/faaliyetlerimiz"
            className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span>Tümünü Görüntüle</span>
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}